import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Navigation: React.FC = () => {
    const location = useLocation();
    const { user, signOut } = useAuth();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    const isActive = (path: string) => location.pathname === path;

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSignOut = async () => {
        try {
            await signOut();
            setShowUserMenu(false);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    return (
        <nav className="hidden md:block bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0">
                <div className="text-center md:text-left">
                    <Link to="/courses" className="hover:opacity-80 transition-opacity">
                        <h1 className="text-3xl md:text-3xl font-semibold text-text mb-1">
                            Attenflow
                        </h1>
                    </Link>
                </div>
                
                <div className="flex items-center space-x-6">
                    {/* Navigation Links */}
                    <div className="flex space-x-6">
                        <Link
                            to="/tasks"
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isActive('/tasks') ? 'bg-primary text-primary-text shadow-md' : 'text-text hover:bg-accent/20'}`}
                        >
                            Tasks
                        </Link>
                        <Link
                            to="/courses"
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isActive('/courses') ? 'bg-primary text-primary-text shadow-md' : 'text-text hover:bg-accent/20'}`}
                        >
                            Courses
                        </Link>
                        <Link
                            to="/pomodoro"
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isActive('/pomodoro') ? 'bg-primary text-primary-text shadow-md' : 'text-text hover:bg-accent/20'}`}
                        >
                            Pomodoro
                        </Link>
                        {/*<Link*/}
                        {/*    to="/projects"*/}
                        {/*    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isActive('/projects') ? 'bg-primary text-primary-text shadow-md' : 'text-text hover:bg-accent/20'}`}*/}
                        {/*>*/}
                        {/*    Projects*/}
                        {/*</Link>*/}
                    </div>

                    {/* User Menu */}
                    {user && (
                        <div className="relative" ref={userMenuRef}>
                            <button
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            >
                                {user.photoURL ? (
                                    <img
                                        src={user.photoURL}
                                        alt={user.displayName || 'User'}
                                        className="w-8 h-8 rounded-full"
                                    />
                                ) : (
                                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                                        {user.displayName?.[0] || user.email?.[0] || 'U'}
                                    </div>
                                )}
                                <span className="text-sm font-medium text-gray-700 hidden md:block">
                                    {user.displayName || user.email}
                                </span>
                                <svg
                                    className={`w-4 h-4 text-gray-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Dropdown Menu */}
                            {showUserMenu && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                                        <p className="font-medium">{user.displayName || 'User'}</p>
                                        <p className="text-gray-500 text-xs">{user.email}</p>
                                    </div>
                                    <Link
                                        to="/settings/profile"
                                        onClick={() => setShowUserMenu(false)}
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                                    >
                                        ⚙️ Settings
                                    </Link>
                                    <button
                                        onClick={handleSignOut}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                                    >
                                        Sign Out
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Navigation; 